import { computed, nextTick, ref, toRef, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { useRoute } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { helpers, required, sameAs } from '@vuelidate/validators'
import { isEmpty, isNumber as lodashIsNumber } from 'lodash'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useCatalogStore } from '@/modules/billing/stores/catalog-store'
import { initialMoney, Money, toMoney, toNumber } from '@/modules/shared/utils/money'
import { parse } from '@/modules/shared/utils/v-table'
import { EntityExpanded } from '../stores/better-entity-store'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { AddOn, Subscription } from '../types'
import { format } from 'date-fns'

// -----------------------------------------------------------------------------
// Config
// -----------------------------------------------------------------------------

const LEGAL_NAME_SUFFIX = 'A Series of V360 Holdings LLC'

////////////////////////////////////////////////////////////////
//// Utils
////////////////////////////////////////////////////////////////

const isVenture360Admin = () => form.value.is_venture360_the_administrator
const isNumber = (value: any) => value === null || value === '' || !isNaN(Number(value))

const mustBeNumber = helpers.withMessage('Must be a number', isNumber)
const mustBeBetween = (min: number, max: number) =>
  helpers.withMessage('Must be between 0 and 100', (value: any) => value >= min && value <= max)
const required_if_V360Admin_and_value_is_present = (type?: string) =>
  helpers.withMessage('Value is required', (value: any) => {
    if (!isVenture360Admin()) return true
    let isValid = false

    switch (type) {
      case 'boolean':
        isValid = typeof value === 'boolean'
        break
      case 'number':
        isValid = isNumber(value)
        break
      default:
        isValid = !!value
        break
    }
    return isValid
  })

// V360 document validation helpers - called from generateRules with the form type
const createV360DocumentValidators = (type: 'create' | 'edit') => ({
  required_if_creating_with_v360_documents: () =>
    helpers.withMessage('Required when using Venture360 structure documents', (value: any) => {
      if (type === 'edit') return true // Skip validation on edit
      if (!form.value.is_venture360_providing_formation_documents) return true
      return !isEmpty(value)
    }),
  required_number_if_creating_with_v360_documents: () =>
    helpers.withMessage('Required when using Venture360 structure documents', (value: any) => {
      if (type === 'edit') return true // Skip validation on edit
      if (!form.value.is_venture360_providing_formation_documents) return true
      return lodashIsNumber(value)
    }),
})

////////////////////////////////////////////////////////////////
//// Options
////////////////////////////////////////////////////////////////

const legalStructureOptions = [
  'LLC',
  'LP',
  'LLP',
  'Ltd',
  'Non-Profit Corporation',
  'C-Corp',
  'S-Corp',
  'Pte Ltd',
  'Pty Ltd',
  'Trust',
  'IRA',
  'ROTH IRA',
  'Disregarded Entity',
  'Other',
].map((structure) => ({
  label: structure,
  value: structure,
}))

const generateAdminOptions = (investorStore) =>
  computed(() =>
    investorStore.individuals
      .map((investor) => ({
        label: investor.name,
        value: investor.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

const generateManagersOptions = (investorStore) =>
  computed(() =>
    (investorStore.investors || [])
      .map((investor) => ({
        label: investor.name,
        value: investor._cid,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

const activityOptions = [
  'New investor additions',
  'Capital calls',
  'Follow-on investments',
  'Distributions to investors',
  'Interest transfers',
  'Expecting a K-1 from an investment',
  'Pass through',
  'Other',
].map((activity) => ({
  label: activity,
  value: activity,
}))

const investmentTypeOptions = ['Equity', 'Convertible Note', 'SAFE', 'Other'].map((type) => ({
  label: type,
  value: type,
}))

const ownershipTypeOptions = [
  { label: 'By commitment', value: 'committed' },
  { label: 'By capital invested', value: 'capital_called' },
  { label: 'Custom', value: 'assigned' },
]

const sectorOptions = [
  'Artificial Intelligence',
  'Biotech / Life Sciences',
  'Climate / Clean Energy',
  'Consumer Products',
  'Cybersecurity',
  'Defense / Aerospace',
  'E-Commerce',
  'EdTech',
  'FinTech',
  'FoodTech',
  'Gaming',
  'Hardware',
  'HealthTech',
  'Infrastructure',
  'InsurTech',
  'Logistics / Supply Chain',
  'Manufacturing',
  'MarketPlace',
  'Media / Entertainment',
  'PropTech / Real Estate',
  'Robotics',
  'SaaS',
  'SpaceTech',
  'Telecom',
  'Transportation',
  'Web3 / Blockchain',
  'Other',
].map((s) => ({ label: s, value: s }))

const visibilityOptions = [
  { label: 'All Investors', value: 'all_investors' },
  { label: 'Selected Investors', value: 'selected' },
]

const investmentStageOptions = ['Pre-Seed', 'Seed', 'Early', 'Growth', 'Pre-IPO', 'Secondaries'].map((s) => ({
  label: s,
  value: s,
}))

// Catalog-backed refs — populated from useCatalogStore in the composable.
// Module-level so they're shared (matching the existing pattern for form/isRiaMode).
const _catalogSubscriptions = ref<Subscription[]>([])
const _catalogAddOns = ref<AddOn[]>([])
const _catalogPlanKeyToValue = ref<Record<string, string>>({})

////////////////////////////////////////////////////////////////
//// Form State & Validation
////////////////////////////////////////////////////////////////

const initialState = {
  // spv type configuration fields
  is_venture360_the_administrator: true,
  is_venture360_providing_formation_documents: null,
  expect_any_activity: null,
  kind_of_activity: [],
  raising_status: null as string | null,
  investor_range: null as string | null,
  is_raising_under_250k: null as boolean | null,

  // General Structure fields
  ownership_type: 'capital_called' as 'committed' | 'capital_called' | 'assigned',
  name: null,
  legal_name: null,
  manager_id: null,
  manager_type: null,
  admin_signer_id: null,
  admin_ids: [],
  management_fee_percentage: 0,
  carried_interest: 0,
  preferred_return: 0,
  currency: 'USD',

  tax_id: null,
  state_of_incorporation: 'DE',
  type: 'LLC',
  date: format(new Date(), 'yyyy-MM-dd'),

  // Address fields
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  county: '',
  country: 'US',

  // Investment Details fields
  company_id: null as string | null,
  company_name: null,
  company_description: null,
  company_website: null,
  sector: null,
  raised_amount: initialMoney,
  minimum_investment_amount: initialMoney,
  pre_money_valuation_amount: initialMoney,
  expected_close_date: null,
  type_of_investment: null,
  other_investment_type: null,
  fund_type: null as string | null,
  investment_stage: null as string | null,
  visibility: 'all_investors' as 'all_investors' | 'selected',
  invited_investor_ids: [] as string[],

  // Disbursement Details fields
  is_requesting_bank_connection: true,
  disbursement_method: 'wire',
  disbursement_wire_bank_name: '',
  disbursement_wire_bank_address: '',
  disbursement_wire_bank_routing_number: '',
  disbursement_wire_bank_account_number: '',
  disbursement_wire_repeat_bank_account_number: '',
  disbursement_wire_bank_swift_code: '',
  disbursement_wire_account_name: '',
  disbursement_wire_for_further_credit_to: '',
  disbursement_wire_bank_notes: '',
  capital_call_wiring_instructions: null,

  // Tax Section fields
  gosystem_locator: null,
  is_filing_required: true,
  not_filing_reason: null,
  state_filing: null,
  basis: 'Cash',
  starting_tax_year: null,
  ending_tax_year: null,

  // subscription
  selected_services: ['Blue Sky Filings', 'Form D'],
}

const original_bank_account_number = ref('')
const isLoadingEntityData = ref(false)
const createInitialFormState = () => ({ ...initialState })
const resetFormState = () => {
  form.value = createInitialFormState()
  original_bank_account_number.value = ''
  entityLifecycleState.value = null
}
const form = ref(createInitialFormState())
const isRiaMode = ref(false)
const entityLifecycleState = ref<string | null>(null)
const check_bank_account_number = computed(
  () => original_bank_account_number.value !== form.value.disbursement_wire_bank_account_number,
)
const generateRules = (authStore, type: 'create' | 'edit', isConfigDisabled: MaybeRefOrGetter<boolean>) => {
  const { required_if_creating_with_v360_documents, required_number_if_creating_with_v360_documents } =
    createV360DocumentValidators(type)

  return {
    // spv type configuration fields
    is_venture360_the_administrator: { required },
    is_venture360_providing_formation_documents: {
      requiredIf: helpers.withMessage('Value is required', (value: any) => {
        if (!isVenture360Admin()) return true
        if (toValue(isConfigDisabled)) return true
        return typeof value === 'boolean'
      }),
    },
    expect_any_activity: {
      requiredIf: helpers.withMessage('Value is required', (value: any) => {
        if (!isVenture360Admin()) return true
        if (toValue(isConfigDisabled)) return true
        return typeof value === 'boolean'
      }),
    },
    kind_of_activity: {
      minLength: helpers.withMessage('Value is required', (value: any) => {
        if (!isVenture360Admin()) return true
        if (toValue(isConfigDisabled)) return true
        if (!form.value.expect_any_activity) return true

        return !!value?.length
      }),
    },
    raising_status: {
      required: helpers.withMessage('Please select a raising status', (value: any) => {
        if (toValue(isConfigDisabled)) return true
        return !!value
      }),
    },
    investor_range: {
      required: helpers.withMessage('Please select an investor range', (value: any) => {
        if (toValue(isConfigDisabled)) return true
        if (!isVenture360Admin()) return true
        return !!value
      }),
    },
    is_raising_under_250k: {
      required: helpers.withMessage('Please select a raise size', (value: any) => {
        if (toValue(isConfigDisabled)) return true
        if (!isVenture360Admin()) return true
        return typeof value === 'boolean'
      }),
    },

    // General Structure fields
    name: { required },
    legal_name: { required },
    manager_id: {
      requiredIf: helpers.withMessage('Required when using Venture360 structure documents', (value: any) => {
        if (type === 'edit') return true // Skip validation on edit
        if (!form.value.is_venture360_providing_formation_documents) return true
        return !!value
      }),
    },
    admin_signer_id: { required },
    admin_ids: { required, minLength: helpers.withMessage('Need at least 1 admin', (value: any) => !!value?.length) },
    management_fee_percentage: {
      mustBeNumber,
      between: mustBeBetween(0, 100),
      requiredIf: required_number_if_creating_with_v360_documents(),
    },
    carried_interest: {
      mustBeNumber,
      between: mustBeBetween(0, 100),
      requiredIf: required_number_if_creating_with_v360_documents(),
    },
    preferred_return: {
      mustBeNumber,
      between: mustBeBetween(0, 100),
    },
    date: {
      requiredIf: required_if_creating_with_v360_documents(),
    },
    company_name: {
      required: helpers.withMessage('Company Name is required', required),
    },
    tax_id: {
      minLength: helpers.withMessage('Must be at least 9 characters', (value: any) => {
        if (!value) return true
        if (form.value.country !== 'US') return true
        return value.toString().replace(/\D/g, '').length >= 9
      }),
    },

    // Disbursement Details fields
    is_requesting_bank_connection: {
      requiredIf: helpers.withMessage('Value is required', (value: any) =>
        type === 'edit' ? typeof value === 'boolean' : true,
      ),
    },
    disbursement_wire_repeat_bank_account_number: {
      sameAsRepeat: sameAs(
        computed(() =>
          check_bank_account_number.value
            ? form.value.disbursement_wire_bank_account_number
            : form.value.disbursement_wire_repeat_bank_account_number,
        ),
      ),
    },

    // Tax Section fields
    is_filing_required: {
      requiredIf: helpers.withMessage('Value is required', (value: any) => {
        if (!authStore.is_site_admin) return true
        if (!isVenture360Admin()) return true
        return typeof value === 'boolean'
      }),
    },
    not_filing_reason: {
      requiredIf: helpers.withMessage('Value is required', (value: any) => {
        if (!authStore.is_site_admin) return true
        if (!isVenture360Admin()) return true
        if (form.value.is_filing_required) return true
        return !!value
      }),
    },

    // subscription
    selected_services: {
      requiredIf: required_if_V360Admin_and_value_is_present(),
      minLength: helpers.withMessage('Need at least 1 plan', (value: any) => {
        if (!isVenture360Admin()) return true
        if (!isPaymentOptionsAvailable.value) return true
        const services = [...value].filter((service) => !addOnOptions.value.some((option) => option.value === service))
        return !!services.length
      }),
    },
  }
}

////////////////////////////////////////////////////////////////
//// Computed
////////////////////////////////////////////////////////////////

const subscriptionOptions = computed(() => _catalogSubscriptions.value)

// New plan accessibility based on activity, investor range, and raise size
const availablePlanKeys = computed<string[]>(() => {
  const activity = form.value.expect_any_activity
  const range = form.value.investor_range
  const under250k = form.value.is_raising_under_250k

  // Activity expected → Premiere only
  if (activity === true) return ['premiere']

  // Need investor range to determine plans
  if (!range) return []

  // 51-99 investors → Premiere only
  if (range === '51-99') return ['premiere']

  // 31-50 investors → Standard, Premiere
  if (range === '31-50') return ['standard', 'premiere']

  // 1-30 investors — depends on raise size
  if (range === '1-30') {
    if (under250k === null || under250k === undefined) return []
    if (under250k === true) return ['micro', 'standard', 'premiere']
    return ['standard', 'premiere']
  }

  return []
})

const recommendedPlanKey = computed<string | null>(() => {
  const available = availablePlanKeys.value
  if (!available.length) return null

  const activity = form.value.expect_any_activity
  const range = form.value.investor_range
  const under250k = form.value.is_raising_under_250k

  if (activity === true) return 'premiere'
  if (range === '51-99') return 'premiere'
  if (range === '31-50') return 'standard'
  if (range === '1-30' && under250k === true) return 'micro'
  if (range === '1-30' && under250k === false) return 'standard'

  return null
})

const addOnOptions = computed(() => _catalogAddOns.value)

const isPaymentOptionsAvailable = computed(() => {
  return availablePlanKeys.value.length > 0
})
const totalDue = computed((): Money => {
  let total = 0
  const addOnValues = addOnOptions.value.map((a) => a.value)

  form.value.selected_services.forEach((service) => {
    // For RIA, skip add-ons — they're bundled in the RIA plan price
    if (isRiaMode.value && addOnValues.includes(service)) {
      return
    }

    const allPaymentOptions = [...subscriptionOptions.value, ...addOnOptions.value]
    const price = allPaymentOptions.find((option) => option.value === service)?.price

    if (price) {
      const priceNumber = parse(price, 'currency') as number
      total += priceNumber
    }
  })
  return toMoney(total, form.value.currency)
})

////////////////////////////////////////////////////////////////
//// Main
////////////////////////////////////////////////////////////////

const initializeSpvForm = (investorStore, catalogStore, slug: string) => async () => {
  await Promise.all([
    investorStore.fetchIndividuals('all'),
    investorStore.fetchInvestors(),
    catalogStore.fetchCatalog(slug),
  ])
}

const parseManager = () => {
  const rawManagerId = form.value.manager_id
  if (!rawManagerId) return { manager_id: null, manager_type: null }

  const [rawType, id] = rawManagerId.split(':')
  const manager_type = rawType === 'individual' ? 'Investor' : 'InvestorSet'

  return { manager_id: id, manager_type }
}

const buildPayload = () => {
  const { manager_id, manager_type } = parseManager()
  const addOnValues = addOnOptions.value.map((a) => a.value)
  const plans = subscriptionOptions.value.map((s) => s.value).concat(addOnValues)
  // Include add-ons in selected_services (backend filters them out for RIA when billing)
  const selected_services = form.value.selected_services.filter((s) => plans.includes(s))
  const currency = form.value.currency
  const is_requesting_bank_connection = form.value.is_venture360_the_administrator
    ? form.value.is_requesting_bank_connection
    : false

  const isV360Administrator = form.value.is_venture360_the_administrator
  const isUsingV360Documents = form.value.is_venture360_providing_formation_documents === true
  const shouldAppendLegalNameSuffix = isV360Administrator && isUsingV360Documents

  let legal_name = form.value.legal_name
  if (shouldAppendLegalNameSuffix) {
    legal_name = `${form.value.legal_name} ${LEGAL_NAME_SUFFIX}`
  }

  const payload = {
    id: null,
    entity_type: 'spv',
    lifecycle_state: entityLifecycleState.value ?? (form.value.raising_status === 'closed' ? 'closed' : 'raising'),
    investor_range: form.value.investor_range,
    is_raising_under_250k: form.value.is_raising_under_250k,
    is_venture360_the_administrator: form.value.is_venture360_the_administrator,
    is_venture360_providing_formation_documents: form.value.is_venture360_providing_formation_documents,
    investor_set_profile_attributes: {
      is_requesting_bank_connection: is_requesting_bank_connection,
    },
    deal_card_attributes: {
      company_id: form.value.company_id,
      name: form.value.company_name,
      description: form.value.company_description,
      website: form.value.company_website,
      sectors: form.value.sector ? [form.value.sector] : [],
      investment_type: form.value.type_of_investment,
      other_investment_type: form.value.other_investment_type,
      target_raise_amount: toNumber(form.value.raised_amount),
      minimum_commitment_amount: toNumber(form.value.minimum_investment_amount),
      pre_money_valuation_amount: toNumber(form.value.pre_money_valuation_amount),
      expected_close_date: form.value.expected_close_date,
      currency,
      visibility: form.value.visibility,
      invited_investor_ids: form.value.visibility === 'selected' ? form.value.invited_investor_ids : [],
      fund_type: form.value.fund_type,
      investment_stage: form.value.investment_stage,
    },
    expect_any_activity: form.value.expect_any_activity,
    kind_of_activity: form.value.kind_of_activity,
    ownership_type: form.value.ownership_type,
    name: form.value.name,
    date: form.value.date,
    legal_name,
    manager_id,
    manager_type,
    admin_signer_id: form.value.admin_signer_id,
    admin_ids: form.value.admin_ids,
    management_fee_percentage: form.value.management_fee_percentage,
    carried_interest: form.value.carried_interest,
    preferred_return: form.value.preferred_return,
    currency,
    street1: form.value.street1,
    street2: form.value.street2,
    city: form.value.city,
    state: form.value.state,
    zip: form.value.zip,
    county: form.value.county,
    country: form.value.country,
    gosystem_locator: form.value.gosystem_locator,
    is_filing_required: form.value.is_filing_required,
    not_filing_reason: form.value.not_filing_reason,
    basis: form.value.basis,
    state_filing: form.value.state_filing,
    starting_tax_year: form.value.starting_tax_year,
    ending_tax_year: form.value.ending_tax_year,
    selected_services: form.value.is_venture360_the_administrator ? selected_services : [],
    tax_id: form.value.tax_id,
    state_of_incorporation: form.value.state_of_incorporation,
    type: form.value.type,
    disbursement_method: form.value.disbursement_method,
    disbursement_wire_bank_name: form.value.disbursement_wire_bank_name,
    disbursement_wire_bank_address: form.value.disbursement_wire_bank_address,
    disbursement_wire_bank_routing_number: form.value.disbursement_wire_bank_routing_number,
    disbursement_wire_bank_account_number: form.value.disbursement_wire_bank_account_number,
    disbursement_wire_bank_swift_code: form.value.disbursement_wire_bank_swift_code,
    disbursement_wire_account_name: form.value.disbursement_wire_account_name,
    disbursement_wire_for_further_credit_to: form.value.disbursement_wire_for_further_credit_to,
    disbursement_wire_bank_notes: form.value.disbursement_wire_bank_notes,
    capital_call_wiring_instructions: form.value.capital_call_wiring_instructions,
  }

  return payload
}

const setFormWithEntityData = (entity: EntityExpanded) => {
  if (!entity) return
  isLoadingEntityData.value = true
  const profile = entity.investor_set_profile || ({} as any)
  const address = entity.address || ({} as any)

  // spv configuration fields
  entityLifecycleState.value = entity.lifecycle_state
  form.value.raising_status = entity.lifecycle_state === 'raising' ? 'currently_raising' : 'closed'
  form.value.investor_range = entity.investor_range || null
  form.value.is_raising_under_250k = entity.is_raising_under_250k ?? null
  form.value.is_venture360_the_administrator = entity.is_venture360_the_administrator
  form.value.is_venture360_providing_formation_documents = entity.is_venture360_providing_formation_documents
  form.value.expect_any_activity = entity.expect_any_activity
  form.value.kind_of_activity = entity.kind_of_activity || []

  // general structure details
  form.value.ownership_type = entity.ownership_type || 'capital_called'
  form.value.name = entity.name
  form.value.legal_name = entity.legal_name.split(LEGAL_NAME_SUFFIX)[0].trim()
  form.value.manager_id = entity.manager_id ? `${entity.manager_type}:${entity.manager_id}` : null
  form.value.manager_type = entity.manager_type
  form.value.admin_signer_id = entity.admin_signer_id
  form.value.admin_ids = entity.admins?.map((admin) => admin.id) || []
  form.value.management_fee_percentage = entity.management_fee_percentage
  form.value.carried_interest = entity.carried_interest
  form.value.preferred_return = entity.preferred_return_percentage
  form.value.currency = entity.currency
  form.value.tax_id = entity.tax_id || null
  form.value.state_of_incorporation = entity.state_of_incorporation || null
  form.value.type = entity.type
  form.value.date = entity.date_established
  form.value.date_exited = entity.date_exited || null

  // address details
  form.value.street1 = address.line1
  form.value.street2 = address.line2
  form.value.city = address.city
  form.value.state = address.state
  form.value.zip = address.zip
  form.value.county = address.county
  form.value.country = address.country || 'US'

  // investment details (from deal card)
  const dealCard = entity.deal_card
  if (dealCard) {
    form.value.company_id = dealCard.company_id || null
    form.value.company_name = dealCard.name || null
    form.value.company_description = dealCard.description || null
    form.value.company_website = dealCard.website || null
    form.value.sector = dealCard.sectors?.[0] ?? null
    form.value.type_of_investment = dealCard.investment_type || null
    form.value.other_investment_type = dealCard.other_investment_type || null
    form.value.raised_amount = dealCard.target_raise
      ? toMoney(dealCard.target_raise.amount, dealCard.target_raise.currency)
      : initialMoney
    form.value.minimum_investment_amount = dealCard.minimum_commitment
      ? toMoney(dealCard.minimum_commitment.amount, dealCard.minimum_commitment.currency)
      : initialMoney
    form.value.pre_money_valuation_amount = dealCard.pre_money_valuation
      ? toMoney(dealCard.pre_money_valuation.amount, dealCard.pre_money_valuation.currency)
      : initialMoney
    form.value.expected_close_date = dealCard.expected_close_date || null
    form.value.visibility = dealCard.visibility || 'all_investors'
    form.value.invited_investor_ids = dealCard.invited_investor_ids || []
    form.value.fund_type = dealCard.fund_type || null
    form.value.investment_stage = dealCard.investment_stage || null
  }

  // banking details
  form.value.is_requesting_bank_connection = profile.is_requesting_bank_connection
  form.value.disbursement_method = 'wire'
  form.value.disbursement_wire_bank_name = entity.disbursement_wire_bank_name
  form.value.disbursement_wire_bank_address = entity.disbursement_wire_bank_address
  form.value.disbursement_wire_bank_routing_number = entity.disbursement_wire_bank_routing_number
  form.value.disbursement_wire_bank_account_number = entity.disbursement_wire_bank_account_number
  form.value.disbursement_wire_bank_swift_code = entity.disbursement_wire_bank_swift_code
  form.value.disbursement_wire_account_name = entity.disbursement_wire_account_name
  form.value.disbursement_wire_for_further_credit_to = entity.disbursement_wire_for_further_credit_to
  form.value.disbursement_wire_bank_notes = entity.disbursement_wire_bank_notes
  original_bank_account_number.value = entity.disbursement_wire_bank_account_number
  form.value.capital_call_wiring_instructions = entity.capital_call_wiring_instructions

  // tax details
  form.value.gosystem_locator = entity.gosystem_locator
  form.value.is_filing_required = entity.is_filing_required
  form.value.not_filing_reason = entity.not_filing_reason
  form.value.state_filing = entity.state_filing
  form.value.basis = entity.basis
  form.value.starting_tax_year = entity.starting_tax_year || null
  form.value.ending_tax_year = entity.ending_tax_year || null

  form.value.selected_services = entity.selected_services || []
  nextTick(() => {
    isLoadingEntityData.value = false
  })
}

const isRecommended = (subscription: Subscription) => {
  const key = Object.entries(_catalogPlanKeyToValue.value).find(([, v]) => v === subscription.value)?.[0]
  return key === recommendedPlanKey.value
}

const isAccessible = (subscription: Subscription) => {
  const key = Object.entries(_catalogPlanKeyToValue.value).find(([, v]) => v === subscription.value)?.[0]
  return key ? availablePlanKeys.value.includes(key) : false
}

const useFormWatchers = () => {
  watch(
    () => form.value.is_venture360_the_administrator,
    () => {
      if (isLoadingEntityData.value) return
      if (form.value.is_venture360_the_administrator) {
        form.value.currency = 'USD'
      }
      form.value.is_filing_required = form.value.is_venture360_the_administrator
      form.value.is_venture360_providing_formation_documents = null
      form.value.expect_any_activity = null
      form.value.kind_of_activity = []

      form.value.selected_services = addOnOptions.value.map((a) => a.value)
    },
  )
  watch(
    () => form.value.is_venture360_providing_formation_documents,
    () => {
      if (isLoadingEntityData.value) return
      form.value.expect_any_activity = null
      form.value.kind_of_activity = []
    },
  )
  watch(
    () => form.value.expect_any_activity,
    () => {
      if (isLoadingEntityData.value) return
      form.value.kind_of_activity = []
    },
  )
  watch(
    () => form.value.date,
    (newDate) => {
      if (isLoadingEntityData.value) return
      if (!newDate) return
      form.value.starting_tax_year = new Date(newDate).getFullYear()
    },
    { immediate: true },
  )
}

export const useSpvForm = (
  type: 'create' | 'edit',
  isConfigDisabled: MaybeRefOrGetter<boolean>,
  isRIA: MaybeRefOrGetter<boolean> = false,
) => {
  const authStore = useAuthStore()
  const investorStore = useInvestingInvestorStore()
  const catalogStore = useCatalogStore()
  const route = useRoute()
  const slug = route.params.slug as string

  const initialize_spv_form = initializeSpvForm(investorStore, catalogStore, slug)
  const rules = generateRules(authStore, type, isConfigDisabled)
  const v$ = useVuelidate(rules, form, { $lazy: true })
  const adminOptions = generateAdminOptions(investorStore)
  const managerOptions = generateManagersOptions(investorStore)
  const investorOptions = computed(() =>
    investorStore.individuals
      .map((investor) => ({
        label: investor.name,
        value: investor.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

  // Sync isRIA parameter with module-level isRiaMode ref
  watch(
    () => toValue(isRIA),
    (value) => {
      isRiaMode.value = value
    },
    { immediate: true },
  )

  // Sync catalog store data to module-level refs
  watch(
    () => catalogStore.subscriptionOptions,
    (val) => {
      _catalogSubscriptions.value = val
    },
    { immediate: true },
  )
  watch(
    () => catalogStore.addOnOptions,
    (val) => {
      _catalogAddOns.value = val
    },
    { immediate: true },
  )
  watch(
    () => catalogStore.planKeyToValue,
    (val) => {
      _catalogPlanKeyToValue.value = val
    },
    { immediate: true },
  )

  // Clear state_of_incorporation when country changes — the state options list
  // is shared with the address state and repopulates per country, so a stale
  // code from the previous country would no longer have a matching label.
  watch(
    () => form.value.country,
    (newCountry, prevCountry) => {
      if (isLoadingEntityData.value) return
      if (!prevCountry || newCountry === prevCountry) return
      form.value.state_of_incorporation = null
    },
  )

  return {
    form,
    v$,
    // Options
    adminOptions,
    managerOptions,
    activityOptions,
    investmentTypeOptions,
    ownershipTypeOptions,
    subscriptionOptions,
    addOnOptions,
    legalStructureOptions,
    sectorOptions,
    visibilityOptions,
    investmentStageOptions,
    investorOptions,

    // Computed
    isPaymentOptionsAvailable,
    totalDue,
    check_bank_account_number,

    // Methods
    initializeSpvForm: initialize_spv_form,
    setFormWithEntityData,
    isRecommended,
    useFormWatchers,
    isAccessible,
    buildPayload,
    resetFormState,
    createInitialFormState,
  }
}
